import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@material-ui/core";

const LEVEL_NAMES = ["Extremely Low", "Low", "Medium", "High", "Extremely High"];
const LEVEL_COLORS = ["#ffcccc", "#ffe0b2", "#fff9c4", "#c8e6c9", "#a5d6a7"];

const DEFAULT_INPUTS = {
  audiogenev9: ["0.6", "0.75", "0.87", "0.93"],
  audiogenev4: ["0.60", "0.72", "0.86", "0.91"],
};

// Expected CSV columns: id, probability, accuracy
const CSV_PATHS = {
  audiogenev9: "/accuracy_data/audiogenev9",
  audiogenev4: "/accuracy_data/audiogenev4",
};

const parseCSV = (text) => {
  const lines = text.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1)
    .filter((line) => line.trim() !== "")
    .map((line) => {
      const values = line.split(",").map((v) => v.trim());
      return Object.fromEntries(headers.map((h, i) => [h, values[i]]));
    })
    .map((row) => ({
      id: row.id,
      probability: parseFloat(row.probability),
      accuracy: row.accuracy.trim().toLowerCase() === "true" ? 1 : 0,
    }))
    .filter((row) => !isNaN(row.probability));
};

const getBucketIndex = (score, thresholds) => {
  for (let i = 0; i < thresholds.length; i++) {
    if (score < thresholds[i]) return i;
  }
  return thresholds.length;
};

const ConfidenceThresholdAnalyzer = () => {
  const [model, setModel] = useState("audiogenev9");
  const [csvData, setCsvData] = useState({ audiogenev9: null, audiogenev4: null });
  const [loadState, setLoadState] = useState({ audiogenev9: "idle", audiogenev4: "idle" });
  const [inputValues, setInputValues] = useState({
    audiogenev9: [...DEFAULT_INPUTS.audiogenev9],
    audiogenev4: [...DEFAULT_INPUTS.audiogenev4],
  });

  // Fetch the CSV for a model the first time it's selected
  useEffect(() => {
    if (csvData[model] !== null || loadState[model] === "loading") return;

    setLoadState((prev) => ({ ...prev, [model]: "loading" }));
    fetch(CSV_PATHS[model])
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        setCsvData((prev) => ({ ...prev, [model]: parseCSV(text) }));
        setLoadState((prev) => ({ ...prev, [model]: "loaded" }));
      })
      .catch(() => {
        setCsvData((prev) => ({ ...prev, [model]: [] }));
        setLoadState((prev) => ({ ...prev, [model]: "error" }));
      });
  }, [model]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentInputs = inputValues[model];
  const parsedThresholds = currentInputs.map(parseFloat);

  const thresholdErrors = parsedThresholds.map((v, i) => {
    if (isNaN(v) || v < 0 || v > 1) return "Must be 0 – 1";
    if (i > 0 && v <= parsedThresholds[i - 1]) return "Must exceed previous cutoff";
    return null;
  });

  const isValid = thresholdErrors.every((e) => e === null);

  const handleInputChange = (index, value) => {
    setInputValues((prev) => {
      const updated = { ...prev, [model]: [...prev[model]] };
      updated[model][index] = value;
      return updated;
    });
  };

  const rows = csvData[model] ?? [];

  const buckets = useMemo(() => {
    if (!isValid || rows.length === 0) return null;
    const bins = Array.from({ length: 5 }, () => ({ count: 0, correctSum: 0 }));
    for (const row of rows) {
      const idx = getBucketIndex(row.probability, parsedThresholds);
      bins[idx].count++;
      bins[idx].correctSum += row.accuracy;
    }
    return bins;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isValid, ...parsedThresholds]);

  const totalSamples = buckets ? buckets.reduce((s, b) => s + b.count, 0) : 0;

  const getRangeLabel = (index) => {
    if (!isValid) return "—";
    const lo = index === 0 ? "0" : parsedThresholds[index - 1];
    const hi = index === 4 ? "1" : parsedThresholds[index];
    return `${lo} – ${hi}`;
  };

  const getAccuracyLabel = (bin) => {
    if (!bin || bin.count === 0) return "—";
    return `${((bin.correctSum / bin.count) * 100).toFixed(1)}%`;
  };

  const isLoading = loadState[model] === "loading";
  const isError = loadState[model] === "error";

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 16, marginBottom: 20 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            label="Model"
          >
            <MenuItem value="audiogenev9">AG9</MenuItem>
            <MenuItem value="audiogenev4">AG4</MenuItem>
          </Select>
        </FormControl>

        {currentInputs.map((val, i) => (
          <TextField
            key={`${model}-cutoff-${i}`}
            label={`${LEVEL_NAMES[i]} / ${LEVEL_NAMES[i + 1]}`}
            type="number"
            size="small"
            variant="outlined"
            value={val}
            error={!!thresholdErrors[i]}
            helperText={thresholdErrors[i] ?? " "}
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            style={{ width: 200 }}
            onChange={(e) => handleInputChange(i, e.target.value)}
          />
        ))}
      </Box>

      {/* Status messages */}
      {isLoading && (
        <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" style={{ color: "gray" }}>Loading accuracy data…</Typography>
        </Box>
      )}
      {isError && (
        <Typography variant="body2" style={{ color: "#c62828", marginBottom: 12 }}>
          Could not load accuracy data for <code>{model}</code>. Check that the file exists in <code>server/data/</code>.
        </Typography>
      )}

      {/* Results table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: "#e0e0e0" }}>
              <TableCell><strong>Level</strong></TableCell>
              <TableCell><strong>Score Range</strong></TableCell>
              <TableCell><strong>Sample Count</strong></TableCell>
              <TableCell><strong>% of Samples</strong></TableCell>
              <TableCell><strong>Accuracy</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {LEVEL_NAMES.map((name, i) => {
              const bin = buckets?.[i];
              return (
                <TableRow key={name} style={{ backgroundColor: LEVEL_COLORS[i] }}>
                  <TableCell><strong>{name}</strong></TableCell>
                  <TableCell>{getRangeLabel(i)}</TableCell>
                  <TableCell>{bin ? bin.count : "—"}</TableCell>
                  <TableCell>
                    {bin && totalSamples > 0
                      ? `${((bin.count / totalSamples) * 100).toFixed(1)}%`
                      : "—"}
                  </TableCell>
                  <TableCell>{getAccuracyLabel(bin)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ConfidenceThresholdAnalyzer;
